import React, { useState, useEffect } from 'react';
import api from '../api';

export default function ApprovalGateway() {
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchDraft(); }, []);

  async function fetchDraft() {
    setLoading(true);
    try {
      const { data } = await api.get('/orders/draft');
      setOrder(data.order);
      setItems(data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load draft order');
    } finally {
      setLoading(false);
    }
  }

  async function regenerate() {
    setLoading(true);
    try {
      const { data } = await api.post('/orders/draft/regenerate');
      setOrder(data.order);
      setItems(data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to regenerate order');
    } finally {
      setLoading(false);
    }
  }

  async function approve() {
    if (!order?.id) return;
    setApproving(true);
    setError('');
    try {
      await api.post(`/orders/${order.id}/approve`, { approved: true });
      setApproved(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Approval failed');
    } finally {
      setApproving(false);
    }
  }

  function updateQty(idx, qty) {
    const updated = items.map((item, i) =>
      i === idx ? { ...item, quantity_to_order: parseFloat(qty) || 0 } : item
    );
    setItems(updated);
  }

  if (loading) return <div className="loading">Generating order...</div>;

  if (approved) {
    return (
      <div className="approval-card" style={{ maxWidth: 600, margin: '0 auto' }}>
        <div className="approval-header" style={{ background: '#0d9488' }}>
          <h2>✅ Order Approved & Sent</h2>
          <p>Vendors have been notified. Order is in transit.</p>
        </div>
        <div className="card-body text-center" style={{ padding: 40 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🌺</div>
          <p className="text-gray">Orders sent to {items.filter(i => i.vendor_name).length} local vendors and mainland suppliers.</p>
          <button className="btn btn-ghost mt-4" onClick={() => { setApproved(false); fetchDraft(); }}>
            View New Draft
          </button>
        </div>
      </div>
    );
  }

  const localCount = items.filter(i => i.source === 'local').length;
  const mainlandCount = items.filter(i => i.source === 'mainland').length;

  return (
    <div className="approval-card" style={{ maxWidth: 680, margin: '0 auto' }}>
      <div className="approval-header">
        <div className="flex justify-between items-center">
          <div>
            <h2>🌺 Draft Order</h2>
            <p>{items.length} items · {localCount} local · {mainlandCount} mainland</p>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ color: '#9ca3af', borderColor: '#4b5563' }} onClick={regenerate}>
            ↻ Regenerate
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ margin: '12px 24px 0' }}>{error}</div>}

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="emoji">🎉</div>
          <p>All items are at or above par. No order needed.</p>
        </div>
      ) : (
        <div className="approval-items">
          {items.map((item, idx) => (
            <div className="order-item-row" key={idx}>
              <div className="item-info">
                <div className="item-name">{item.item_name}</div>
                <div className="item-meta">
                  On hand: {item.quantity_on_hand} {item.unit} · Par: {item.par_level} {item.unit}
                  {item.avg_daily_sold > 0 && ` · Avg ${item.avg_daily_sold.toFixed(1)}/day`}
                </div>
                {item.vendor_name && (
                  <div className="item-meta" style={{ color: '#0d9488' }}>
                    📍 {item.vendor_name}
                  </div>
                )}
              </div>
              <div className="item-qty">
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={item.quantity_to_order}
                  onChange={(e) => updateQty(idx, e.target.value)}
                  style={{ width: 72, textAlign: 'right', padding: '4px 8px', fontSize: 16, fontWeight: 700 }}
                />
                <div className="text-xs text-gray">{item.unit}</div>
              </div>
              <div className="item-source">
                <span className={`badge ${item.source === 'local' ? 'badge-teal' : 'badge-gray'}`}>
                  {item.source === 'local' ? '🌱 local' : '✈️ mainland'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="approval-footer">
          <div className="text-sm text-gray">
            Tap Approve to send orders to all vendors.
            <br />No orders fire without your approval.
          </div>
          <button
            className="approve-btn"
            onClick={approve}
            disabled={approving || items.length === 0}
          >
            {approving ? 'Sending...' : '✓ Approve & Send'}
          </button>
        </div>
      )}
    </div>
  );
}
